import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { StoreUser } from '../services/authService';
import API_URLS from '../config/apiConfig';
import api from '../interceptors/axiosConfig';
import type { ManualOnboardingField, OnboardingStatusResponse } from '../types/features/storeDetails/onboarding';

interface UseStoreOnboardingStatusParams {
    storeId?: string;
    isSuperAdmin: boolean;
    shouldLoad: boolean;
    storeUsers: StoreUser[];
}

const useStoreOnboardingStatus = ({
    storeId,
    isSuperAdmin,
    shouldLoad,
    storeUsers,
}: UseStoreOnboardingStatusParams) => {
    const [onboardingLoading, setOnboardingLoading] = useState(false);
    const [onboardingError, setOnboardingError] = useState('');
    const [onboardingData, setOnboardingData] = useState<OnboardingStatusResponse | null>(null);
    const [onboardingLoadAttempted, setOnboardingLoadAttempted] = useState(false);
    const [onboardingSavingField, setOnboardingSavingField] = useState<ManualOnboardingField | null>(null);
    const [onboardingEmailRecipientStoreUserId, setOnboardingEmailRecipientStoreUserId] = useState<string>('');
    const [onboardingEmailSending, setOnboardingEmailSending] = useState(false);
    const [onboardingEmailResult, setOnboardingEmailResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const onboardingUpdateInFlightRef = useRef(false);

    const onboardingEmailContacts = useMemo(() => {
        return storeUsers.filter((u) => {
            const status = (u.status || '').toLowerCase();
            return status === 'active' && !!u.contact_email;
        });
    }, [storeUsers]);

    useEffect(() => {
        if (!onboardingEmailContacts.length) {
            setOnboardingEmailRecipientStoreUserId('');
            return;
        }
        const owner = onboardingEmailContacts.find((u) => (u.role || '').toLowerCase() === 'owner');
        const defaultRecipient = owner || onboardingEmailContacts[0];
        setOnboardingEmailRecipientStoreUserId((prev) => prev || defaultRecipient.id);
    }, [onboardingEmailContacts]);

    const loadOnboardingStatus = useCallback(async () => {
        if (!storeId) return;
        try {
            setOnboardingLoadAttempted(true);
            setOnboardingLoading(true);
            setOnboardingError('');
            const response = await api.get<OnboardingStatusResponse>(
                `${API_URLS.GET_STORE_ONBOARDING_STATUS(storeId)}?format=json`
            );
            setOnboardingData(response.data);
        } catch (err) {
            console.error('Error loading onboarding status:', err);
            setOnboardingError('Unable to load onboarding status.');
            toast.error('Unable to load onboarding status.');
        } finally {
            setOnboardingLoading(false);
        }
    }, [storeId]);

    const updateOnboardingField = useCallback(
        async (field: ManualOnboardingField, value: boolean) => {
            if (!storeId || !onboardingData) return;
            if (onboardingUpdateInFlightRef.current) return;

            const previous = onboardingData;
            const next: OnboardingStatusResponse = {
                ...previous,
                setup: { ...previous.setup },
                operations: { ...previous.operations },
            };

            if (field in next.setup) {
                (next.setup as any)[field] = value;
            } else {
                (next.operations as any)[field] = value;
            }
            onboardingUpdateInFlightRef.current = true;
            setOnboardingData(next);
            setOnboardingSavingField(field);

            try {
                const response = await api.put<OnboardingStatusResponse>(
                    API_URLS.GET_STORE_ONBOARDING_STATUS(storeId),
                    { [field]: value }
                );
                setOnboardingData(response.data);
            } catch (err) {
                console.error('Error updating onboarding status field:', err);
                setOnboardingData(previous);
                toast.error('Unable to update onboarding status.');
            } finally {
                setOnboardingSavingField(null);
                onboardingUpdateInFlightRef.current = false;
            }
        },
        [storeId, onboardingData]
    );

    const handleSendOnboardingEmail = useCallback(async () => {
        if (!storeId || !onboardingEmailRecipientStoreUserId) {
            toast.error('Select a store contact to send onboarding status.');
            return;
        }
        try {
            setOnboardingEmailSending(true);
            setOnboardingEmailResult(null);
            const response = await api.post<{ success: boolean; message: string; recipient_email: string }>(
                API_URLS.SEND_STORE_ONBOARDING_STATUS_EMAIL(storeId),
                { recipient_store_user_id: onboardingEmailRecipientStoreUserId }
            );
            if (response.data?.success) {
                const successText = `Onboarding status emailed to ${response.data.recipient_email}`;
                setOnboardingEmailResult({ type: 'success', text: successText });
                toast.success(`Onboarding status emailed to ${response.data.recipient_email}`);
            } else {
                setOnboardingEmailResult({ type: 'error', text: 'Failed to send onboarding status email.' });
                toast.error('Failed to send onboarding status email.');
            }
        } catch (err) {
            console.error('Error sending onboarding status email:', err);
            setOnboardingEmailResult({ type: 'error', text: 'Failed to send onboarding status email.' });
            toast.error('Failed to send onboarding status email.');
        } finally {
            setOnboardingEmailSending(false);
        }
    }, [storeId, onboardingEmailRecipientStoreUserId]);

    useEffect(() => {
        setOnboardingLoadAttempted(false);
        setOnboardingError('');
        setOnboardingData(null);
    }, [storeId]);

    useEffect(() => {
        if (!isSuperAdmin || !storeId || !shouldLoad) return;
        if (!onboardingData && !onboardingLoading && !onboardingLoadAttempted) {
            loadOnboardingStatus();
        }
    }, [
        isSuperAdmin,
        storeId,
        shouldLoad,
        onboardingData,
        onboardingLoading,
        onboardingLoadAttempted,
        loadOnboardingStatus,
    ]);

    return {
        onboardingLoading,
        onboardingError,
        onboardingData,
        onboardingSavingField,
        onboardingEmailRecipientStoreUserId,
        onboardingEmailContacts,
        onboardingEmailSending,
        onboardingEmailResult,
        setOnboardingEmailRecipientStoreUserId,
        loadOnboardingStatus,
        updateOnboardingField,
        handleSendOnboardingEmail,
    };
};

export default useStoreOnboardingStatus;
